"use client";

import { useState } from "react";
import { cn } from "@repo/ui/utils";
import { Overline } from "@repo/ui/overline";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@repo/ui/accordion";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import type { ImageContentExpanderProps } from "./image-content-expander.types";

export function ImageContentExpander({
  overline,
  headline,
  body,
  listItems,
  className,
}: ImageContentExpanderProps) {
  const hasTextContent = overline || headline || body;
  const hasListItems = listItems && listItems.length > 0;

  const [openItem, setOpenItem] = useState("");
  const [visibleImage, setVisibleImage] = useState("item-0");

  const handleValueChange = (value: string) => {
    setOpenItem(value);
    if (value) setVisibleImage(value);
  };

  return (
    <section
      className={cn("bg-surface-base-primary", className)}
      data-testid="image-content-expander"
    >
      <div className="container-grid py-16 desktop:py-32">
        <div className="col-span-full grid grid-cols-subgrid gap-y-12 desktop:gap-y-20">
          {/* Top: Text content */}
          {hasTextContent && (
            <div className="col-span-full flex flex-col gap-4 desktop:gap-6 desktop:max-w-[644px]">
              {overline && <Overline>{overline}</Overline>}
              {headline && (
                <h2 className="typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 text-text-primary">
                  {headline}
                </h2>
              )}
              {body && (
                <RichTextCondensed
                  richText={body}
                  className="text-text-secondary"
                />
              )}
            </div>
          )}

          {/* Bottom: Image + Accordion */}
          {hasListItems && (
            <div className="col-span-full grid grid-cols-subgrid gap-y-12 desktop:gap-y-0">
              {/* Image — all items stacked, fade between on selection */}
              {hasListItems && listItems.some((item) => item.visual) && (
                <div className="col-span-full desktop:col-span-4 grid grid-cols-1">
                  {listItems.map((item, index) =>
                    item.visual ? (
                      <div
                        key={item._key ?? index}
                        className={cn(
                          "col-start-1 row-start-1 transition-opacity duration-700",
                          `item-${index}` === visibleImage
                            ? "opacity-100"
                            : "opacity-0 pointer-events-none",
                        )}
                      >
                        <VisualWrapper
                          visual={item.visual}
                          className="w-full aspect-[358/240] desktop:aspect-[420/465] overflow-hidden rounded-md"
                          visualClassName="w-full h-full"
                          imgClassName="object-cover"
                        />
                      </div>
                    ) : null,
                  )}
                </div>
              )}

              {/* Accordion */}
              {hasListItems && (
                <div className="col-span-full desktop:col-span-7 desktop:col-start-6">
                  <Accordion
                    type="single"
                    collapsible
                    value={openItem}
                    onValueChange={handleValueChange}
                  >
                    {listItems.map((item, index) => (
                      <AccordionItem
                        key={item._key ?? index}
                        value={`item-${index}`}
                      >
                        <AccordionTrigger className="[&>svg]:hidden gap-4 py-6 md:py-6 md:gap-4 [&[data-state=open]]:pb-4">
                          <span className="flex gap-4 items-start w-full text-left">
                            <span className="typography-body-sm-medium text-text-primary w-9 desktop:w-14 shrink-0">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                            {item.headline && (
                              <span className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary">
                                {item.headline}
                              </span>
                            )}
                          </span>
                        </AccordionTrigger>
                        {item.body && (
                          <AccordionContent>
                            <div className="flex gap-4">
                              <div className="w-9 desktop:w-14 shrink-0" />
                              <RichTextCondensed
                                richText={item.body}
                                className="text-text-secondary"
                              />
                            </div>
                          </AccordionContent>
                        )}
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
